// src/module/time-attendance/hooks/useAttendanceTimer.js
import { useEffect, useMemo, useRef, useState } from 'react'

export default function useAttendanceTimer({
    attendanceId,
    clockinCreatedAt,
    hasClockIn,
    hasClockOut,
    isPaused,
    pausedAt,
    totalPausedSeconds = 0,
}) {
    const [seconds, setSeconds] = useState(0)
    const intervalRef = useRef(null)

    const clockinDate = useMemo(() => {
        if (!clockinCreatedAt) return null
        const d = new Date(clockinCreatedAt)
        return isNaN(d.getTime()) ? null : d
    }, [clockinCreatedAt])

    const pausedAtDate = useMemo(() => {
        if (!pausedAt) return null
        const d = new Date(pausedAt)
        return isNaN(d.getTime()) ? null : d
    }, [pausedAt])

    const computeSeconds = () => {
        if (!clockinDate) return 0

        const now = Date.now()
        const rawElapsed = Math.max(0, Math.floor((now - clockinDate.getTime()) / 1000))

        const accPaused = Math.max(0, Number(totalPausedSeconds || 0))

        const pauseInProgress =
            isPaused && pausedAtDate ? Math.max(0, Math.floor((now - pausedAtDate.getTime()) / 1000)) : 0

        return Math.max(0, rawElapsed - (accPaused + pauseInProgress))
    }

    useEffect(() => {
        if (!attendanceId || !hasClockIn || hasClockOut || !clockinDate) {
            setSeconds(0)
            if (intervalRef.current) clearInterval(intervalRef.current)
            intervalRef.current = null
            return
        }

        setSeconds(computeSeconds())

        if (intervalRef.current) clearInterval(intervalRef.current)
        intervalRef.current = null
    }, [attendanceId])

    useEffect(() => {
        if (!attendanceId || !hasClockIn || hasClockOut || !clockinDate) {
            if (intervalRef.current) clearInterval(intervalRef.current)
            intervalRef.current = null
            return
        }

        if (isPaused) {
            if (intervalRef.current) clearInterval(intervalRef.current)
            intervalRef.current = null
            setSeconds(computeSeconds())
            return
        }

        if (intervalRef.current) clearInterval(intervalRef.current)
        intervalRef.current = setInterval(() => {
            setSeconds(computeSeconds())
        }, 1000)

        return () => {
            if (intervalRef.current) clearInterval(intervalRef.current)
            intervalRef.current = null
        }
    }, [attendanceId, hasClockIn, hasClockOut, clockinDate, isPaused, pausedAt, totalPausedSeconds])

    return seconds
}
